/*
  related-rails.js
  ---------------------------------------------------------------
  Fills product-page rails ([data-rail]) with product cards fetched
  from a filtered collection URL. Two modes:

  1) PARTIAL MODE - the rail carries data-rail-partial
     (sections/prints-come-in.liquid, the modular Related Products
     Rail). data-rail-url is fetched through the Section Rendering
     API partial (sections/swatch-products-partial.liquid) and its
     #swatch-partial-grid fragment is read:
       - data-products on the grid is the FILTERED product count.
         A count above the guard threshold means the storefront
         ignored the filter param (that Search and Discovery filter
         is not configured yet) and returned the whole pool - the
         result is not actually filtered, so it is discarded.
       - each .swatch-partial__item wraps one product card PLUS its
         inline boot script; both are moved into the rail track so
         the card gallery still initialises. Scripts inserted via
         innerHTML/appendChild of parsed nodes never execute, so
         each one is re-run by cloning it into a fresh element.
     If the guard trips (or nothing usable comes back) and the rail
     has a data-rail-fallback-url, that URL is tried once - it uses
     the legacy facet (filter.p.product_type) that already works, so
     the rail keeps working until the new metafield filters go live,
     then upgrades automatically.

  2) LEGACY MODE - no data-rail-partial
     (sections/collection-product.liquid). Fetches the raw collection
     URL and scrapes #product-grid for .card-product-slider nodes,
     exactly as before. Kept byte-for-byte compatible.

  Shared rail attributes:
    data-rail-url           the filtered URL to fetch
    data-current-handle     current product's handle (excluded)
    data-rail-limit         max cards to show (default 12)
  Partial mode only:
    data-rail-partial       Section Rendering API section id
                            (default swatch-products-partial)
    data-rail-fallback-url  retry-once URL when the primary result
                            is unusable
    data-rail-min           minimum cards required to reveal the
                            rail (default 2) - fewer keeps it hidden
    data-rail-guard         optional override for the guard count
                            (default 2000)
  Legacy mode only:
    data-current-type       current product's type (lowercased compare)
    data-rail-exclude-type  "true" drops cards whose type matches
  A rail that ends up with no usable cards is hidden (display:none
  on its closest .shopify-section).
*/
(function () {
  if (window.__relatedRailsBooted) return;
  window.__relatedRailsBooted = true;

  var GUARD_MAX = 2000;

  // Scripts inserted via parsed-HTML nodes never execute - clone each
  // into a fresh element so the per-card boot scripts run.
  function executeScripts(container) {
    container.querySelectorAll('script').forEach(function (oldScript) {
      var newScript = document.createElement('script');
      Array.prototype.forEach.call(oldScript.attributes, function (attr) {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  function hideRail(rail) {
    var sec = rail.closest('.shopify-section');
    if (sec) { sec.style.display = 'none'; } else { rail.style.display = 'none'; }
  }

  function handleFromCard(card) {
    if (card.dataset.productHandle) return card.dataset.productHandle;
    var a = card.querySelector('a[href*="/products/"]');
    if (!a) return '';
    return a.getAttribute('href').split('?')[0].split('/products/')[1] || '';
  }

  // Nudge the native scroll-snap row enhancer so its prev/next arrows
  // recompute now that the row actually has content.
  function nudgeScroller(track) {
    var scroller = track.closest('[data-row-scroller]');
    if (scroller) {
      try { scroller.dispatchEvent(new Event('scroll')); } catch (e) {}
      window.dispatchEvent(new Event('resize'));
    }
  }

  /* ---------------- partial mode ---------------- */

  function fetchPartialItems(url, partialId, guardMax) {
    var sep = url.indexOf('?') >= 0 ? '&' : '?';
    return fetch(url + sep + 'section_id=' + encodeURIComponent(partialId), {
      headers: { 'Accept': 'text/html' }
    })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var grid = doc.getElementById('swatch-partial-grid');
        if (!grid) return { guardTripped: false, items: [] };
        // Exact ignored-filter detector: the partial emits the filtered
        // count (data-total) and the unfiltered collection size (data-all).
        // Equal counts mean the storefront ignored the filter param (that
        // Search and Discovery filter is not configured yet), so the result
        // is the whole pool, not a filtered set.
        var filtered = parseInt(grid.getAttribute('data-total'), 10);
        var unfiltered = parseInt(grid.getAttribute('data-all'), 10);
        if (!isNaN(filtered) && !isNaN(unfiltered) && unfiltered > 0 && filtered >= unfiltered) {
          return { guardTripped: true, items: [] };
        }
        var total = parseInt(grid.getAttribute('data-products'), 10) || 0;
        if (total > guardMax) return { guardTripped: true, items: [] };
        var items = Array.prototype.slice.call(grid.querySelectorAll('.swatch-partial__item'));
        return { guardTripped: false, items: items };
      });
  }

  function initPartialRail(rail, track) {
    var primaryUrl = rail.dataset.railUrl;
    var fallbackUrl = rail.dataset.railFallbackUrl || '';
    var partialId = rail.dataset.railPartial || 'swatch-products-partial';
    var currentHandle = rail.dataset.currentHandle || '';
    var excludeTypeTitle = (rail.dataset.excludeTypeTitle || '').trim().toLowerCase();
    var excludeTypeLegacy = (rail.dataset.excludeTypeLegacy || '').trim().toLowerCase();
    var limit = parseInt(rail.dataset.railLimit, 10) || 12;
    var minCards = parseInt(rail.dataset.railMin, 10) || 2;
    var guardMax = parseInt(rail.dataset.railGuard, 10) || GUARD_MAX;

    // Guard-tripped results contribute nothing; otherwise drop the card
    // for the product being viewed, plus (when data-exclude-type-title is
    // set) any card whose product_type metaobject title matches the
    // current product's - runs before the limit slice so excluded cards
    // don't eat rail slots. Cards missing the metaobject fall back to the
    // legacy product.type compare so backfill gaps can't leak through.
    function usableItems(result) {
      if (result.guardTripped) return [];
      var exSet = {};
      if (excludeTypeTitle) exSet[excludeTypeTitle] = true;
      if (excludeTypeLegacy) exSet[excludeTypeLegacy] = true;
      var hasExclusion = excludeTypeTitle || excludeTypeLegacy;
      return result.items.filter(function (item) {
        var card = item.querySelector('.card-product-slider');
        var handle = card ? handleFromCard(card) : '';
        if (handle && currentHandle && handle === currentHandle) return false;
        if (hasExclusion && card) {
          var typeTitle = (card.dataset.typeTitle || '').trim().toLowerCase();
          if (typeTitle) {
            if (exSet[typeTitle]) return false;
          } else {
            var legacy = (card.dataset.productType || '').trim().toLowerCase();
            if (legacy && exSet[legacy]) return false;
          }
        }
        return true;
      });
    }

    fetchPartialItems(primaryUrl, partialId, guardMax)
      .then(function (result) {
        var items = usableItems(result);
        if ((result.guardTripped || items.length === 0) && fallbackUrl) {
          return fetchPartialItems(fallbackUrl, partialId, guardMax).then(usableItems);
        }
        return items;
      })
      .then(function (items) {
        items = items.slice(0, limit);
        if (items.length < minCards) { hideRail(rail); return; }
        items.forEach(function (item) {
          var slide = document.createElement('div');
          slide.className = 'swiper-slide';
          while (item.firstChild) slide.appendChild(item.firstChild);
          track.appendChild(slide);
        });
        executeScripts(track);
        rail.removeAttribute('hidden');
        nudgeScroller(track);
      })
      .catch(function () { hideRail(rail); });
  }

  /* ------- legacy mode (collection-product.liquid) ------- */

  function initLegacyRail(rail, track) {
    var url = rail.dataset.railUrl;
    var currentHandle = rail.dataset.currentHandle || '';
    var excludeType = rail.dataset.railExcludeType === 'true';
    var currentType = (rail.dataset.currentType || '').trim().toLowerCase();
    var limit = parseInt(rail.dataset.railLimit, 10) || 12;

    fetch(url, { headers: { 'Accept': 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var grid = doc.getElementById('product-grid');
        if (!grid) { hideRail(rail); return; }

        var cards = Array.prototype.slice.call(grid.querySelectorAll('.card-product-slider'));
        var added = 0;
        for (var i = 0; i < cards.length && added < limit; i++) {
          var card = cards[i];
          var handle = handleFromCard(card);
          if (handle && currentHandle && handle === currentHandle) continue;
          if (excludeType && currentType && (card.dataset.productType || '').trim().toLowerCase() === currentType) continue;

          var slide = document.createElement('div');
          slide.className = 'swiper-slide';
          slide.appendChild(card);
          track.appendChild(slide);
          added++;
        }

        if (added === 0) { hideRail(rail); return; }

        executeScripts(track);
        rail.removeAttribute('hidden');
        nudgeScroller(track);
      })
      .catch(function () { hideRail(rail); });
  }

  function initRail(rail) {
    if (rail.dataset.railLoaded) return;
    rail.dataset.railLoaded = '1';

    var track = rail.querySelector('[data-rail-track]');
    if (!rail.dataset.railUrl || !track) return;

    if (rail.hasAttribute('data-rail-partial')) {
      initPartialRail(rail, track);
    } else {
      initLegacyRail(rail, track);
    }
  }

  function bootRails(scope) {
    (scope || document).querySelectorAll('[data-rail]').forEach(initRail);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bootRails(document); });
  } else {
    bootRails(document);
  }
  document.addEventListener('shopify:section:load', function (e) { bootRails(e.target); });
})();
